import React, {useEffect, useState} from 'react';
import {Button, OutlinedInputProps, TextField, IconButton, Tooltip, Box} from '@mui/material';
import DeleteIconOutline from "@mui/icons-material/DeleteOutline";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import {UpdateSportObjectInput, useGetCoordsByAddressLazyQuery} from "../../../../../graphql/graphQlApiHooks";
import {useStores} from "../../../../../store";
import makeStyles from "@mui/styles/makeStyles";
import {Theme} from "@mui/material/styles";
import {observer} from "mobx-react-lite";

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        height: '100%',
        overflow: 'auto'
    },
    inputWrapper: {
        '& .MuiFormControl-root': {
            marginBottom: '1em',
        },
        '& .MuiFilledInput-root': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            borderRadius: theme.shape.borderRadius,
            transition: 'background-color 0.2s ease',
            '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
            },
            '&.Mui-focused': {
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
            },
            '& .MuiFilledInput-input': {
                color: 'white',
                padding: '16px 12px',
                fontSize: '14px',
                '&::placeholder': {
                    color: 'rgba(255, 255, 255, 0.7)',
                    opacity: 1,
                }
            }
        },
        '& .MuiInputLabel-root': {
            color: 'rgba(255, 255, 255, 0.8)',
            fontSize: '14px',
            '&.Mui-focused': {
                color: 'white',
            }
        },
    },
    addressFieldWrapper: {
        position: 'relative',
        '& .MuiFormControl-root': {
            marginBottom: '1em',
        },
    },
    locationButton: {
        position: 'absolute',
        right: '8px',
        top: '50%',
        transform: 'translateY(-50%)',
        zIndex: 1,
        color: 'rgba(255, 255, 255, 0.7)',
        '&:hover': {
            color: 'white',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
        },
    }
}))

const EditSportObject = observer(({
    open,
    onCompleted,
    deleteOnClicked
}: {
    open: boolean,
    onCompleted: () => void,
    deleteOnClicked: () => void
}) => {
    const {settingsStore} = useStores();
    const classes = useStyles();
    const [getCoordsByAddress, { loading: coordsLoading }] = useGetCoordsByAddressLazyQuery();

    const [name, setName] = useState('');
    const [address, setAddress] = useState('');
    const [longitude, setLongitude] = useState<number | null>(null);
    const [latitude, setLatitude] = useState<number | null>(null);
    const [activeDirectoryGroup, setActiveDirectoryGroup] = useState('');
    

    useEffect(() => {
 
        
        if (settingsStore.sportObject) {
            const nameValue = settingsStore.sportObject.name || '';
            const addressValue = settingsStore.sportObject.address || '';
            const longitudeValue = settingsStore.sportObject.longitude || null;
            const latitudeValue = settingsStore.sportObject.latitude || null;
            const activeDirectoryGroupValue = settingsStore.sportObject.activeDirectoryGroup || '';
   
            setName(nameValue);
            setAddress(addressValue);
            setLongitude(longitudeValue);
            setLatitude(latitudeValue);
            setActiveDirectoryGroup(activeDirectoryGroupValue);
        }
    }, [settingsStore.sportObject, open])

    const onInputName = ({target: {value}}: any) => setName(value);
    const onInputAddress = ({target: {value}}: any) => setAddress(value);
    
    // Функция для получения координат по адресу
    const handleGetCoordsByAddress = async () => {
        if (!address.trim()) {
            return;
        }

        try {
            const result = await getCoordsByAddress({
                variables: { address: address.trim() }
            });

            if (result.data?.getCoordsByAddress) {
                const { longitude: lng, latitude: lat } = result.data.getCoordsByAddress;
                if (lng !== null && lat !== null) {
                    setLongitude(lng);
                    setLatitude(lat);
                } else {
                    // Если координаты не найдены, используем координаты центра Москвы
                    const moscowCoords = {
                        longitude: 37.6173,
                        latitude: 55.7558
                    };
                    setLongitude(moscowCoords.longitude);
                    setLatitude(moscowCoords.latitude);
                }
            }
        } catch (error) {
            console.error('Ошибка при получении координат:', error);
            // В случае ошибки используем координаты центра Москвы
            const moscowCoords = {
                longitude: 37.6173,
                latitude: 55.7558
            };
            setLongitude(moscowCoords.longitude);
            setLatitude(moscowCoords.latitude);
        }
    };
    
    const onInputLongitude = ({target: {value}}: any) => setLongitude(value ? parseFloat(value) : null);
    const onInputLatitude = ({target: {value}}: any) => setLatitude(value ? parseFloat(value) : null);
    const onInputActiveDirectoryGroup = ({target: {value}}: any) => setActiveDirectoryGroup(value);

    const onSubmitEdit = (e: any) => {
        e.preventDefault()
        if (name !== '') {
            settingsStore.sportObjectUpdate({
                id: settingsStore.sportObject.id,
                name,
                address,
                longitude,
                latitude,
                activeDirectoryGroup
            } as UpdateSportObjectInput).then(() => {
                onCompleted()
            })
        }
    };

    return <>
        <form
            id="edit_object"
            className={classes.root}
            onSubmit={onSubmitEdit}
        >

            <div className={classes.inputWrapper}>
                <TextField
                    name='name'
                    id="edit_name"
                    label="Название"
                    placeholder="Добавьте название"
                    variant="filled"
                    size={'small'}
                    autoComplete={'off'}
                    value={name}
                    onChange={onInputName}
                    fullWidth={true}
                    multiline={true}
                    maxRows={3}
                    InputProps={{disableUnderline: true} as Partial<OutlinedInputProps>}

                />
                <Box className={classes.addressFieldWrapper}>
                    <TextField
                        name='address'
                        id="edit_address"
                        label="Адрес"
                        placeholder="Добавьте адрес"
                        variant="filled"
                        size={'small'}
                        autoComplete={'off'}
                        value={address}
                        onChange={onInputAddress}
                        fullWidth={true}
                        multiline={true}
                        maxRows={2}
                        InputProps={{disableUnderline: true} as Partial<OutlinedInputProps>}
                    />
                    <Tooltip title="Определить координаты по адресу">
                        <span>
                            <IconButton
                                className={classes.locationButton}
                                onClick={handleGetCoordsByAddress}
                                disabled={coordsLoading || !address.trim()}
                                size="small"
                            >
                                <LocationOnIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
                <TextField
                    name='longitude'
                    id="edit_longitude"
                    label="Долгота"
                    placeholder="0.000000"
                    variant="filled"
                    size={'small'}
                    autoComplete={'off'}
                    value={longitude || ''}
                    onChange={onInputLongitude}
                    type="number"
                    fullWidth={true}
                    InputProps={{
                        disableUnderline: true,
                        inputProps: { step: "any" }
                    } as Partial<OutlinedInputProps>}
                />
                <TextField
                    name='latitude'
                    id="edit_latitude"
                    label="Широта"
                    placeholder="0.000000"
                    variant="filled"
                    size={'small'}
                    autoComplete={'off'}
                    value={latitude || ''}
                    onChange={onInputLatitude}
                    type="number"
                    fullWidth={true}
                    InputProps={{
                        disableUnderline: true,
                        inputProps: { step: "any" }
                    } as Partial<OutlinedInputProps>}
                />
                <TextField
                    name='activeDirectoryGroup'
                    id="activeDirectoryGroup"
                    label="Группа в Active Directory"
                    placeholder="Укажите группу в Active Directory"
                    variant="filled"
                    size={'small'}
                    autoComplete={'off'}
                    value={activeDirectoryGroup}
                    onChange={onInputActiveDirectoryGroup}
                    fullWidth={true}
                    InputProps={{disableUnderline: true} as Partial<OutlinedInputProps>}
                />

            </div>
            <Button
                size={'large'}
                color="inherit"
                variant="text"
                startIcon={<DeleteIconOutline/>}
                onClick={deleteOnClicked}
                fullWidth={true}
            >
                Удалить
            </Button>
        </form>
    </>;
})

// EditSportObject.protoTypes = {}

export default EditSportObject;
